// YourAccountDrawer.tsx · v12.10.28 (#2187 Mobile uses the shared PhoneInput) · 2026-11-01 · VoiceSpectrum AI
// YourAccountDrawer.tsx · v12.10.15 (#1637 Follow-ups count - several pills; All clears the others and any other clears All) · 2026-10-13 · VoiceSpectrum AI
// YourAccountDrawer.tsx · v12.10.14 (#1622 Preferences as chips - T2_UI_PRINCIPLES v1.24 §46) · 2026-10-12 · VoiceSpectrum AI
// YourAccountDrawer.tsx · v12.10.12 (#1534 Preferences › Follow-ups count: Overdue / Today / This week / Future / All, default Today) · 2026-10-10 · VoiceSpectrum AI
// YourAccountDrawer.tsx · v12.10.9.4 (#1342 F9 password rules; #1336 no sample text in sign-in boxes; #1343 newest 3, up to 15; #1357 US spelling) · 2026-10-07 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · YourAccountDrawer · v12.10.9.3 (#1306 camera button on the photo: Upload / Change / Remove, kept in the database; #1307 Recent security activity - last 10, newest first) · 2026-10-07
// VoiceSpectrum.Web.T2 · v12.10.9.1 (#1288 'This browser is trusted until … · Forget this browser') · 2026-10-06
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// YourAccountDrawer.tsx - "Your account" (#1259, prototype scPaintDrawer in self mode): the person's own profile, their
// group(s) read-only (nobody changes their own groups - §36), sign-in and 2-step (set up / replace authenticator, set up text
// code, new backup codes, change password), passkeys + security keys FUTURE, Notifications FUTURE.
//
// Open it from anywhere with openYourAccount() - it mounts in its own root on <body> (no router / query provider needed),
// so the profile menu in Shell.tsx can call it directly. <YourAccountDrawer onClose/> also works in-tree.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import { clearTrust, readTrust, signedInSlug } from '../../lib/authApi';
import { accountAuthApi, normalizePolicy, securityApi, securityErrorMessage, type ActivityItem, type Group, type Policy, type UserRow } from '../../lib/securityApi';
import { PHOTO_HINT, shrinkPhoto } from '../../lib/photo';
import { getCachedMe, notifySecurityChanged, setCachedMe } from '../../lib/useAccess';
import { toast } from '../../lib/toast';
import { FU_PREF_CHOICES, fuPrefLabel, getCachedFuPref, loadFuPref, saveFuPref, toggleFuPref, type FuBadgePref } from '../../lib/followUpsPref';   // v12.10.12 (#1534)
import { AuthenticatorSetup, BackupCodesModal, ChangePasswordModal, TextCodeSetup } from './AccountFlows';
import PhoneInput from '../PhoneInput';   // v12.10.28 (#2187)
import { isConfirmCancelled, withConfirm } from './ConfirmItsYou';
import { Avatar, GroupPill, displayName, enforceShort, groupsLabel, requiredFor } from './people';
import { ConfirmDialog, Ic, InlineError, digits, lastFour, useEscClose, type ConfirmOpts } from './ui';

interface Draft { firstName: string; lastName: string; jobTitle: string; mobile: string }
const draftOf = (u: UserRow): Draft => ({ firstName: u.firstName ?? '', lastName: u.lastName ?? '', jobTitle: u.jobTitle ?? '', mobile: u.mobile ?? '' });

type Flow = null | 'password' | 'totp' | 'sms' | { codes: string[] };

export function YourAccountDrawer({ onClose }: { onClose: () => void }) {
  const [user, setUser] = useState<UserRow | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [policy, setPolicy] = useState<Policy | null>(getCachedMe()?.policy ?? null);
  const [loadErr, setLoadErr] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [flow, setFlow] = useState<Flow>(null);
  const [confirm, setConfirm] = useState<ConfirmOpts | null>(null);
  // v12.10.9.3 (#1306 / #1307)
  const [camOpen, setCamOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [activity, setActivity] = useState<ActivityItem[] | null>(null);
  const [actMore, setActMore] = useState(false);   // v12.10.9.4 (#1343)
  // v12.10.12 (#1534): the Follow-ups count preference.
  const [fuPref, setFuPrefState] = useState<FuBadgePref[]>(getCachedFuPref());   // v12.10.15 (#1637): several pills
  const [fuSaving, setFuSaving] = useState(false);
  useEffect(() => { void loadFuPref(true).then(setFuPrefState); }, []);
  useEffect(() => { securityApi.activity().then((r) => setActivity(r.items ?? [])).catch(() => setActivity([])); }, []);
  async function onPhoto(f: File | undefined) {
    if (fileRef.current) fileRef.current.value = '';
    if (!f) return;
    setPhotoBusy(true);
    try {
      const dataUrl = await shrinkPhoto(f);
      const r = await securityApi.savePhoto(dataUrl);
      setUser((u) => (u ? { ...u, photo: r.photo } : u));
      notifySecurityChanged();
      toast('success', 'Your photo is saved');
    } catch (e) { toast('error', e instanceof Error && !(e as { status?: number }).status ? e.message : securityErrorMessage(e, 'Your photo wasn’t saved. Try again.')); }
    finally { setPhotoBusy(false); }
  }
  async function removePhoto() {
    setCamOpen(false); setPhotoBusy(true);
    try {
      await securityApi.removePhoto();
      setUser((u) => (u ? { ...u, photo: null } : u));
      notifySecurityChanged();
      toast('success', 'Your photo is removed');
    } catch (e) { toast('error', securityErrorMessage(e, 'Your photo wasn’t removed. Try again.')); }
    finally { setPhotoBusy(false); }
  }
  // v12.10.9.1 (#1288): is this browser trusted for the 2-step?
  const [trustedUntil, setTrustedUntil] = useState<string | null>(null);
  useEffect(() => {
    const t = readTrust(signedInSlug());
    if (!t) return;
    securityApi.trustedDevice(t.token)
      .then((r) => { if (r.trusted && r.until) setTrustedUntil(r.until); else clearTrust(signedInSlug()); })
      .catch(() => { /* older API */ });
  }, []);
  async function forgetBrowser() {
    const t = readTrust(signedInSlug());
    try { await securityApi.forgetDevice(t?.token ?? null); } catch { /* still forget locally */ }
    clearTrust(signedInSlug()); setTrustedUntil(null);
    toast('success', 'This browser is forgotten', 'The 2-step will be asked here at your next sign-in.');
  }

  async function load() {
    try {
      const u = await securityApi.account();
      setUser(u);
      setDraft((d) => d ?? draftOf(u));
    } catch (e) {
      setLoadErr(securityErrorMessage(e, 'Your account couldn’t be loaded. Close this and try again.'));
    }
    // Group names: the groups list needs User groups access; without it the pill reads "1 group".
    securityApi.groups().then((r) => setGroups(r.groups ?? [])).catch(() => { /* no access - fine */ });
    if (!getCachedMe()) {
      securityApi.me().then((m) => { setCachedMe(m); setPolicy(normalizePolicy(m.policy)); }).catch(() => { /* older API */ });
    }
  }
  useEffect(() => { void load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // refresh after a 2-step change (the flows fire notifySecurityChanged)
  useEffect(() => {
    const on = () => { securityApi.account().then(setUser).catch(() => { /* keep */ }); };
    window.addEventListener('vs:security-changed', on);
    return () => window.removeEventListener('vs:security-changed', on);
  }, []);

  const dirty = !!user && !!draft && JSON.stringify(draft) !== JSON.stringify(draftOf(user));
  const errs: Record<string, string> = {};
  if (draft) {
    if (!draft.firstName.trim()) errs.firstName = 'First name is needed.';
    if (!draft.lastName.trim()) errs.lastName = 'Last name is needed.';
    const md = digits(draft.mobile);
    if (draft.mobile.trim() && !(md.length === 10 || (md.length === 11 && md.startsWith('1')))) errs.mobile = 'Enter a 10-digit mobile number.';
    if (!draft.mobile.trim() && !user?.email) errs.mobile = 'You sign in with your mobile, so it can’t be empty.';
  }
  const valid = Object.keys(errs).length === 0;

  function requestClose() {
    if (!dirty) { onClose(); return; }
    setConfirm({ sev: 'warn', title: 'Discard your changes?', body: 'You changed your profile but haven’t saved it.', label: 'Discard changes', danger: true, cancelLabel: 'Keep editing', onOk: () => onClose() });
  }
  useEscClose(() => { if (camOpen) { setCamOpen(false); return; } if (!flow && !confirm) requestClose(); }, true);

  async function save() {
    if (!user || !draft || !dirty || !valid || saving) return;
    setSaving(true); setErr('');
    try {
      const body = { firstName: draft.firstName.trim(), lastName: draft.lastName.trim(), mobile: draft.mobile.trim() || null, jobTitle: draft.jobTitle.trim() || null };
      const u = await withConfirm(() => securityApi.updateAccount(body), 'You’re changing your sign-in details.');
      setUser(u); setDraft(draftOf(u));
      notifySecurityChanged();
      toast('success', 'Your profile is saved');
      onClose();
    } catch (e) {
      if (!isConfirmCancelled(e)) setErr(securityErrorMessage(e, 'Your changes weren’t saved. Try again.'));
    } finally { setSaving(false); }
  }

  function newCodes() {
    setConfirm({
      sev: 'info', title: 'Generate new backup codes?',
      body: 'Your ten new codes replace any you have now - old ones stop working. Save or print them straight away; they’re shown once.',
      label: 'Generate codes',
      onOk: async () => {
        const r = await withConfirm(() => accountAuthApi.regenerateBackupCodes(), 'You’re replacing your backup codes.');
        notifySecurityChanged();
        setFlow({ codes: r.backupCodes ?? [] });
      },
      errorText: (e) => (isConfirmCancelled(e) ? null : securityErrorMessage(e, 'New codes couldn’t be made. Try again.')),
    });
  }

  const name = user ? displayName(user) : '';
  const m = user?.methods ?? [];
  const pol = policy;
  const allowTotp = pol ? pol.methods.totp : true;
  const allowSms = pol ? pol.methods.sms : true;
  const allowBackup = pol ? pol.methods.backup : true;
  const required = user ? requiredFor(user, pol, groups) : false;
  const groupLine = user ? (groups.length ? groupsLabel(user.groups, groups) : plural1(user.groups.length)) : '';

  const body = !user || !draft ? (
    <div className="dw-body">{loadErr ? <InlineError text={loadErr} /> : <div className="sec-loading">Loading your account…</div>}</div>
  ) : (
    <div className="dw-body">
      <div className="sc-dw-sec">
        <h5>Profile</h5>
        <div className="sc-pf-head2">
          <div className="sc-pf-left">
            <span className="sc-ring sc-ring--cam">
              <Avatar u={{ ...user, firstName: draft.firstName, lastName: draft.lastName }} />
              <button type="button" className="sc-cam" aria-label="Change your photo" aria-haspopup="menu" aria-expanded={camOpen} disabled={photoBusy} onClick={() => setCamOpen(!camOpen)}>📷</button>
              {camOpen && (
                <div className="sc-cam-menu" role="menu">
                  <div className="h">{PHOTO_HINT}</div>
                  <button type="button" role="menuitem" onClick={() => { setCamOpen(false); fileRef.current?.click(); }}>⬆ {user.photo ? 'Change photo' : 'Upload photo'}</button>
                  {user.photo && <button type="button" role="menuitem" className="bad" onClick={() => void removePhoto()}>✕ Remove photo</button>}
                </div>
              )}
            </span>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => void onPhoto(e.target.files?.[0])} />
            {groups.length > 0 ? <GroupPill ids={user.groups} groups={groups} /> : user.groups.length > 0 ? <span className="sc-rolepill"><span className="rp-n">{groupLine}</span></span> : null}
          </div>
          <div className="sc-pf-grid">
            <Field id="ya-first" label="First name" value={draft.firstName} err={touched.firstName || dirty ? errs.firstName : ''}
              onChange={(v) => setDraft({ ...draft, firstName: v })} onBlur={() => setTouched({ ...touched, firstName: true })} autoComplete="given-name" />
            <Field id="ya-last" label="Last name" value={draft.lastName} err={touched.lastName || dirty ? errs.lastName : ''}
              onChange={(v) => setDraft({ ...draft, lastName: v })} onBlur={() => setTouched({ ...touched, lastName: true })} autoComplete="family-name" />
            <Field id="ya-title" label="Job title" wide value={draft.jobTitle} placeholder="e.g. Physician, Paralegal, Dispatcher"
              onChange={(v) => setDraft({ ...draft, jobTitle: v })} autoComplete="organization-title" />
          </div>
        </div>
        <div className="sc-pf-grid" style={{ marginTop: 12 }}>
          <div className="sc-pf">
            <label htmlFor="ya-email">Email</label>
            <input id="ya-email" className="sc-in" type="email" value={user.email ?? ''} readOnly placeholder="No email" />
            <span className="sc-idst">{user.email ? <><Ic k="check" />Your sign-in email. An administrator can change it in Users.</> : 'An administrator can add one in Users.'}</span>
          </div>
          <Field id="ya-mobile" label="Mobile" type="tel" value={draft.mobile} err={errs.mobile}
            onChange={(v) => setDraft({ ...draft, mobile: v })} autoComplete="tel" />
          <div className="sc-pf w"><span className="sc-idst">Either one signs in, with the password. At least one is needed, and each must be unique in your organization.</span></div>
        </div>
      </div>

      <div className="sc-dw-sec">
        <h5><Ic k="shieldOk" /> Sign-in and 2-step verification</h5>
        <p className="sc-sub">
          {m.length ? `Your second step: ${m.map((x) => (x === 'sms' ? 'a code texted to your mobile' : 'a code from your authenticator app')).join(' or ')}.`
            : required ? `No second step yet. You must set one up by ${enforceShort(pol)}.` : 'No second step set up.'}
        </p>
        {(m.length > 0 || (user.backupLeft ?? 0) > 0) && (
          <div className="sc-list">
            {m.includes('sms') && (
              <div className="sc-li"><span className="ic"><Ic k="sms" /></span><div className="tx"><b>Text message code{m[0] === 'sms' && <span className="sc-m-tag">Primary</span>}</b><span>Mobile ending {lastFour(user.mobile) || '—'}</span></div></div>
            )}
            {m.includes('totp') && (
              <div className="sc-li"><span className="ic"><Ic k="app" /></span><div className="tx"><b>Authenticator app{m[0] === 'totp' && <span className="sc-m-tag">Primary</span>}</b><span>Set up</span></div></div>
            )}
            {(user.backupLeft ?? 0) > 0 && (
              <div className="sc-li"><span className="ic"><Ic k="codes" /></span><div className="tx"><b>Backup codes</b><span>{user.backupLeft} of 10 left</span></div></div>
            )}
          </div>
        )}
        <div className="sc-btnrow">
          {allowTotp && (
            <button type="button" className="sc-secondary" onClick={() => setFlow('totp')}><Ic k={m.includes('totp') ? 'reset' : 'plus'} /> {m.includes('totp') ? 'Replace authenticator app' : 'Set up authenticator app'}</button>
          )}
          {allowBackup && m.length > 0 && (
            <button type="button" className="sc-secondary" onClick={newCodes}><Ic k="codes" /> {(user.backupLeft ?? 0) > 0 ? 'Get new backup codes' : 'Get backup codes'}</button>
          )}
        </div>
        {trustedUntil && (
          <p className="sc-sub" style={{ margin: '8px 0 0' }}>
            This browser is trusted until {new Date(trustedUntil).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - the 2-step won’t be asked here.{' '}
            <button type="button" className="sc-linkbtn" onClick={forgetBrowser}>Forget this browser</button>
          </p>
        )}
        <div className="sc-futline"><Ic k="lock" /><span>Passkeys and security keys</span><span className="sc-future">Future</span></div>
        <div className="sc-btnrow">
          {allowSms && !m.includes('sms') && <button type="button" className="sc-secondary" onClick={() => setFlow('sms')}><Ic k="plus" /> Set up text message code</button>}
          <button type="button" className="sc-secondary" onClick={() => setFlow('password')}><Ic k="key" /> Change password</button>
        </div>
      </div>

      <div className="sc-dw-sec">
        <h5>Recent security activity</h5>
        <p className="sc-sub">Your last 15 sign-ins and security changes, newest first.</p>
        {/* v12.10.9.4 (#1343 F10, UI-PROTO-T40 LOCKED 10/7): the newest 3; "Show N more" up to 15; "Show full history" FUTURE. */}
        {activity === null ? <div className="sec-loading">Loading…</div> : activity.length === 0 ? <p className="sc-sub" style={{ margin: 0 }}>Nothing yet.</p> : (
          <div className="sc-acts">
            {(actMore ? activity : activity.slice(0, 3)).map((a, i) => (
              <div className="sc-act" key={`${a.at}-${i}`}>
                <div><b>{a.title}</b>{a.detail && <span>{a.detail}</span>}</div>
                <span className="when">{new Date(a.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        )}
        {activity && activity.length > 0 && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', margin: '8px 0 0', flexWrap: 'wrap' }}>
            {activity.length > 3 && <button type="button" className="sc-link" onClick={() => setActMore(!actMore)}>{actMore ? 'Show less' : `Show ${activity.length - 3} more`}</button>}
            <span className="sc-link" aria-disabled="true" style={{ opacity: .45, cursor: 'not-allowed' }} title="Comes with the audit log page">Show full history<span className="sc-future">Future</span></span>
          </div>
        )}
        <p className="sc-sub" style={{ margin: '8px 0 0' }}>Something you don’t recognize? Change your password and tell your administrator.</p>
      </div>

      {/* v12.10.12 (#1534, UI-PROTO-12.10.11.4 v1.6 LOCKED): Preferences - what the left-menu Follow-ups number counts, and what
          the Follow-ups list opens on. Saved the moment it is picked (it is not part of "Save changes"). */}
      <div className="sc-dw-sec">
        <h5>Preferences</h5>
        <b style={{ fontSize: 14 }}>Follow-ups count</b>
        <p className="sc-sub" style={{ margin: '2px 0 6px' }}>What the number beside Follow-ups in the left menu counts, and what the Follow-ups list opens on.</p>
        {/* v12.10.14 (#1622, T2_UI_PRINCIPLES v1.24 §46): Preferences are chips - the choices on one line (the date quick-pick look), the
            picked chip in the light-indigo tint, "Default: X" in grey after them. Never radios spread across the width. */}
        <div role="group" aria-label="Follow-ups count" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {FU_PREF_CHOICES.map((c) => {
            const on = fuPref.includes(c.value);
            return (
              <button key={c.value} type="button" aria-pressed={on} disabled={fuSaving}
                style={{ height: 30, padding: '0 11px', borderRadius: 10, border: `1px solid ${on ? '#c4b5fd' : '#e1e6f0'}`, background: on ? '#ede9fe' : '#fff',
                  color: on ? '#4338ca' : '#44546a', font: 'inherit', fontSize: 12.5, fontWeight: on ? 700 : 400, cursor: fuSaving ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                onClick={() => { const next = toggleFuPref(fuPref, c.value); if (next.join() === fuPref.join()) return; setFuSaving(true); saveFuPref(next).then((v) => { setFuPrefState(v); toast('success', `Saved - the Follow-ups number now counts: ${fuPrefLabel(v)}`); }).catch(() => toast('error', 'Your choice wasn’t saved. Try again.')).finally(() => setFuSaving(false)); }}>
                {c.label}
              </button>
            );
          })}
          <span className="sc-sub" style={{ margin: '0 0 0 4px', fontSize: 12 }}>Pick one or more · All clears the others · Default: Today</span>
        </div>
      </div>

      <div className="sc-dw-sec">
        <h5>Notifications <span className="sc-future">Future</span></h5>
        <p className="sc-sub" style={{ margin: 0 }}>How you’re told about urgent messages and follow-ups - email, text or desktop alerts. Coming in a later release.</p>
      </div>
      {err && <InlineError text={err} />}
    </div>
  );

  return (
    <>
      {createPortal(
        <div className="vs-sec sc-drawer-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
          <div className="sc-drawer" role="dialog" aria-modal="true" aria-label="Your account">
            <div className="dw-head">
              <div className="sc-dw-id"><div style={{ minWidth: 0 }}>
                <h4>Your account</h4>
                <p>{[name, groupLine, user?.jobTitle].filter(Boolean).join(' · ')}</p>
              </div></div>
              <button type="button" className="modal-x" onClick={requestClose} aria-label="Close"><Ic k="x" /></button>
            </div>
            {body}
            <div className="dw-foot">
              <button type="button" className="mc" onClick={requestClose}>{dirty ? 'Cancel' : 'Close'}</button>
              <button type="button" className="ma" disabled={!dirty || !valid || saving} onClick={() => void save()}><Ic k="check" /> {saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {flow === 'password' && user && (
        <ChangePasswordModal minLength={pol?.minLength ?? 12} complexity={!!pol?.complexity} charMix={pol?.charMix}
          nameParts={[user.firstName, user.lastName, (user.email ?? '').split('@')[0]]} onClose={() => setFlow(null)} />
      )}
      {flow === 'totp' && user && <AuthenticatorSetup replacing={m.includes('totp')} who={name} onClose={() => setFlow(null)} />}
      {flow === 'sms' && user && <TextCodeSetup mobile={user.mobile} who={name} onClose={() => setFlow(null)} />}
      {flow && typeof flow === 'object' && <BackupCodesModal codes={flow.codes} who={name} onClose={() => { setFlow(null); toast('success', `${flow.codes.length} new backup codes are active`); }} />}
      {confirm && <ConfirmDialog opts={confirm} onClose={() => setConfirm(null)} />}
    </>
  );
}

function plural1(n: number): string { return n === 1 ? '1 group' : `${n} groups`; }

function Field({ id, label, value, onChange, onBlur, err, placeholder, wide, type = 'text', autoComplete }: {
  id: string; label: string; value: string; onChange: (v: string) => void; onBlur?: () => void; err?: string; placeholder?: string; wide?: boolean; type?: string; autoComplete?: string;
}) {
  return (
    <div className={`sc-pf${wide ? ' w' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {type === 'tel'
        /* v12.10.28 (#2187): a phone field is the app's one PhoneInput (T2_UI_PRINCIPLES §38) - +1 (xxx) xxx-xxxx, leading 1 = country code */
        ? <PhoneInput id={id} className={`sc-in${err ? ' err' : ''}`} value={value} onChange={onChange} onBlur={onBlur} invalid={!!err} />
        : <input id={id} className={`sc-in${err ? ' err' : ''}`} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete}
            onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />}
      {err && <span className="sc-err">{err}</span>}
    </div>
  );
}

// ── imperative opener (profile menu) ───────────────────────────────────────────
let accHost: { el: HTMLDivElement; root: Root } | null = null;
let accHosts = 0;   // in-tree <YourAccountHost/> instances; when one is mounted it handles the event instead
export const OPEN_ACCOUNT_EVENT = 'vs:open-your-account';

/** Open Your account over whatever page is showing. */
export function openYourAccount(): void {
  if (accHosts > 0) { window.dispatchEvent(new CustomEvent(OPEN_ACCOUNT_EVENT)); return; }
  if (accHost) return;
  const el = document.createElement('div');
  el.setAttribute('data-vs-your-account', '');
  document.body.appendChild(el);
  const root = createRoot(el);
  accHost = { el, root };
  const close = () => { const h = accHost; accHost = null; if (h) setTimeout(() => { h.root.unmount(); h.el.remove(); }, 0); };
  root.render(<YourAccountDrawer onClose={close} />);
}

/** Optional in-tree host (e.g. Settings) - listens for openYourAccount() while mounted. */
export function YourAccountHost({ openOnMount = false, onClosed }: { openOnMount?: boolean; onClosed?: () => void }) {
  const [open, setOpen] = useState(openOnMount);
  useEffect(() => {
    accHosts++;
    const on = () => setOpen(true);
    window.addEventListener(OPEN_ACCOUNT_EVENT, on);
    return () => { accHosts--; window.removeEventListener(OPEN_ACCOUNT_EVENT, on); };
  }, []);
  useEffect(() => { if (openOnMount) setOpen(true); }, [openOnMount]);
  return open ? <YourAccountDrawer onClose={() => { setOpen(false); onClosed?.(); }} /> : null;
}

export default YourAccountDrawer;

